// src/components/Calendar.tsx
import React, { useMemo } from "react";
import {
  addDays,
  differenceInMinutes,
  format,
  isSameDay,
  Locale,
  parseISO,
  setHours,
  setMinutes,
  startOfToday,
  startOfWeek,
  subDays,
} from "date-fns";
import { CalendarOff, ChevronLeft, ChevronRight } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { CalendarEvent, CalendarViewType } from "./calendar.ts";

// --- Types ---
interface CalendarProps {
  events: CalendarEvent[];
  /** The displayed day (DAY view) or any day within the displayed week (WEEK view). */
  currentDate: Date;
  onDateChange: (newDate: Date) => void;
  viewType: CalendarViewType;
  locale: Locale;
  onEventClick?: (event: CalendarEvent) => void;
  /** Hours of padding before the first and after the last event. Defaults to 1. */
  timelineThresholdHours?: number;
  /** Height of each hour row in rem. Defaults to 4. */
  hourRowHeightRem?: number;
  /** Whether to render the "Calendar" page heading. Defaults to false (embedded use). */
  showHeading?: boolean;
  /** Message shown (instead of the grid) when the visible range has no events. */
  emptyLabel?: React.ReactNode;
}

// --- Helpers ---
const toDate = (value: Date | string): Date =>
  typeof value === "string" ? parseISO(value) : value;

const getWeekDates = (currentDate: Date, locale: Locale): Date[] => {
  const weekStart = startOfWeek(currentDate, { locale });
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
};

const getHourLabel = (hour: number, locale: Locale): string => {
  const hourDate = setMinutes(setHours(startOfToday(), hour), 0);
  return format(hourDate, "p", { locale });
};

const eventSurfaceClass = (event: CalendarEvent): string =>
  event.color === "secondary"
    ? "border-soluud-secondary bg-soluud-secondary/10"
    : "border-soluud-primary bg-soluud-primary/10";

const eventAccentClass = (event: CalendarEvent): string =>
  event.color === "secondary" ? "text-soluud-secondary" : "text-soluud-primary";

interface PositionedEvent {
  event: CalendarEvent;
  /** Column index within its overlap cluster. */
  col: number;
  /** Total columns in its overlap cluster. */
  cols: number;
}

/**
 * Lays out a day's events so overlapping ones sit side by side instead of
 * stacking on top of each other. Events are grouped into clusters of mutually
 * overlapping events; within a cluster each event gets the first column whose
 * previous event has already ended.
 */
const layoutDayEvents = (events: CalendarEvent[]): PositionedEvent[] => {
  const items = events
    .map((event) => ({
      event,
      start: toDate(event.from).getTime(),
      end: toDate(event.to).getTime(),
      col: 0,
    }))
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const result: PositionedEvent[] = [];
  let cluster: (typeof items)[number][] = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    const columnEnds: number[] = [];
    for (const item of cluster) {
      let placed = false;
      for (let c = 0; c < columnEnds.length; c++) {
        if (columnEnds[c] <= item.start) {
          columnEnds[c] = item.end;
          item.col = c;
          placed = true;
          break;
        }
      }
      if (!placed) {
        item.col = columnEnds.length;
        columnEnds.push(item.end);
      }
    }
    const cols = columnEnds.length;
    for (const item of cluster) {
      result.push({ event: item.event, col: item.col, cols });
    }
  };

  for (const item of items) {
    if (cluster.length > 0 && item.start >= clusterEnd) {
      flush();
      cluster = [];
      clusterEnd = -Infinity;
    }
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.end);
  }
  if (cluster.length > 0) flush();

  return result;
};

// --- Sub-components ---
interface TimelineProps {
  startHour: number;
  endHour: number;
  locale: Locale;
}

const Timeline: React.FC<TimelineProps> = ({ startHour, endHour, locale }) => {
  const hours = [];
  for (let i = startHour; i <= endHour; i++) hours.push(i);

  return (
    <div className="w-12 flex-shrink-0 border-r border-gray-200 sm:w-16">
      {/* Spacer matching the day-header height */}
      <div className="h-16 border-b border-gray-200" />
      {hours.map((hour, index) => (
        <div
          key={`time-${hour}`}
          className="relative h-[var(--hour-row-height)] border-b border-gray-100 pr-1 text-right"
        >
          <span className="text-soluud-text-tertiary absolute -top-2 right-1 text-[10px] sm:text-xs">
            {index === 0 ? "" : getHourLabel(hour, locale)}
          </span>
        </div>
      ))}
    </div>
  );
};

interface EventCardProps {
  event: CalendarEvent;
  timelineStartHour: number;
  hourRowHeightRem: number;
  locale: Locale;
  onClick?: (event: CalendarEvent) => void;
  col: number;
  cols: number;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  timelineStartHour,
  hourRowHeightRem,
  locale,
  onClick,
  col,
  cols,
}) => {
  const start = toDate(event.from);
  const end = toDate(event.to);

  const topOffset =
    (((start.getHours() - timelineStartHour) * 60 + start.getMinutes()) / 60) *
    hourRowHeightRem;
  const height = (differenceInMinutes(end, start) / 60) * hourRowHeightRem;

  const timeRange = `${format(start, "p", { locale })} - ${format(end, "p", { locale })}`;
  const Icon = event.icon;
  const CornerIcon = event.cornerIcon;

  const widthPct = 100 / cols;
  const leftPct = col * widthPct;

  return (
    <button
      type="button"
      onClick={() => onClick?.(event)}
      title={`${timeRange}${event.title ? ` — ${event.title}` : ""}`}
      className={`absolute flex cursor-pointer flex-col overflow-hidden rounded-md border-l-[6px] px-1.5 py-1 text-left leading-tight shadow-[--shadow-card] transition-shadow hover:shadow-[--shadow-card-hover] ${eventSurfaceClass(event)}`}
      style={{
        // top/height/left/width are dynamic, so inline styles are appropriate here.
        top: `${topOffset}rem`,
        height: `${height}rem`,
        minHeight: "2rem",
        left: `calc(${leftPct}% + 2px)`,
        width: `calc(${widthPct}% - 4px)`,
      }}
    >
      {CornerIcon && (
        <CornerIcon
          className={`absolute top-1 right-1 h-3 w-3 shrink-0 ${eventAccentClass(event)}`}
        />
      )}
      <span className="text-soluud-text-primary flex items-center gap-1 pr-3 text-[11px] font-semibold">
        {Icon && <Icon className="h-3 w-3 shrink-0 opacity-60" />}
        <span className="truncate">{timeRange}</span>
      </span>
      {event.title && (
        <span className="text-soluud-text-secondary truncate text-[11px]">
          {event.title}
        </span>
      )}
    </button>
  );
};

const DayHeader: React.FC<{ day: Date; locale: Locale }> = ({
  day,
  locale,
}) => {
  const isToday = isSameDay(day, new Date());
  return (
    <div
      className={`flex h-16 flex-col items-center justify-center border-b border-gray-200 ${isToday ? "bg-soluud-primary-subtle" : ""}`}
    >
      <p className="text-soluud-text-secondary text-xs font-medium sm:text-sm">
        {format(day, "EEE", { locale })}
      </p>
      <p
        className={`text-xl font-semibold sm:text-2xl ${isToday ? "text-soluud-primary" : "text-soluud-text-primary"}`}
      >
        {format(day, "d", { locale })}
      </p>
    </div>
  );
};

interface DayColumnProps {
  day: Date;
  events: CalendarEvent[];
  timelineStartHour: number;
  timelineEndHour: number;
  hourRowHeightRem: number;
  locale: Locale;
  onEventClick?: (event: CalendarEvent) => void;
}

const DayColumn: React.FC<DayColumnProps> = ({
  day,
  events,
  timelineStartHour,
  timelineEndHour,
  hourRowHeightRem,
  locale,
  onEventClick,
}) => {
  const dayEvents = layoutDayEvents(
    events.filter((event) => isSameDay(toDate(event.from), day)),
  );

  const hours = [];
  for (let i = timelineStartHour; i <= timelineEndHour; i++) hours.push(i);

  return (
    <div className="min-w-0 flex-1 border-r border-gray-200 last:border-r-0">
      <DayHeader day={day} locale={locale} />
      <div className="relative">
        {hours.map((hour) => (
          <div
            key={`cell-${format(day, "yyyy-MM-dd")}-${hour}`}
            className="h-[var(--hour-row-height)] border-b border-gray-100"
          />
        ))}
        {dayEvents.map(({ event, col, cols }, idx) => (
          <EventCard
            key={idx}
            event={event}
            timelineStartHour={timelineStartHour}
            hourRowHeightRem={hourRowHeightRem}
            locale={locale}
            onClick={onEventClick}
            col={col}
            cols={cols}
          />
        ))}
      </div>
    </div>
  );
};

// --- Main component ---
const Calendar: React.FC<CalendarProps> = ({
  events,
  currentDate,
  onDateChange,
  viewType,
  locale,
  onEventClick,
  timelineThresholdHours = 1,
  hourRowHeightRem = 4,
  showHeading = false,
  emptyLabel,
}) => {
  const isDayView = viewType === CalendarViewType.DAY;

  const displayDates = useMemo(
    () => (isDayView ? [currentDate] : getWeekDates(currentDate, locale)),
    [isDayView, currentDate, locale],
  );

  const visibleEvents = useMemo(
    () =>
      events.filter((event) =>
        displayDates.some((day) => isSameDay(toDate(event.from), day)),
      ),
    [events, displayDates],
  );
  const hasEvents = visibleEvents.length > 0;

  // Compute the visible hour range from the events shown in the current view,
  // so navigating to an empty week falls back to sensible default hours.
  const { timelineStartHour, timelineEndHour } = useMemo(() => {
    if (visibleEvents.length === 0) {
      return {
        timelineStartHour: Math.max(0, 8 - timelineThresholdHours),
        timelineEndHour: Math.min(23, 17 + timelineThresholdHours),
      };
    }

    let minHour = 23;
    let maxEndHour = 0;

    visibleEvents.forEach((event) => {
      const start = toDate(event.from);
      const end = toDate(event.to);
      minHour = Math.min(minHour, start.getHours());
      // Round the end up to the next hour when it doesn't land on the hour.
      const endHour =
        end.getMinutes() > 0 ? end.getHours() + 1 : end.getHours();
      maxEndHour = Math.max(maxEndHour, endHour);
    });

    return {
      timelineStartHour: Math.max(0, minHour - timelineThresholdHours),
      timelineEndHour: Math.min(23, maxEndHour + timelineThresholdHours),
    };
  }, [visibleEvents, timelineThresholdHours]);

  const step = isDayView ? 1 : 7;
  const goPrevious = () => onDateChange(subDays(currentDate, step));
  const goNext = () => onDateChange(addDays(currentDate, step));
  const goToday = () => onDateChange(new Date());

  return (
    <>
      {showHeading && (
        <h1 className="text-soluud-text-primary mb-6 text-center text-3xl font-bold tracking-tight">
          <Trans>Calendar</Trans>
        </h1>
      )}

      <div
        className="mb-4 flex items-center justify-center gap-2 sm:gap-4"
        aria-label="Calendar navigation"
      >
        <button
          type="button"
          onClick={goPrevious}
          className="bg-soluud-primary hover:bg-soluud-primary-hover active:bg-soluud-primary-active flex items-center justify-center rounded-lg px-3 py-2 text-white shadow-sm transition-all duration-150 active:scale-[0.98]"
          aria-label="Previous"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={goToday}
          className="border-soluud-border text-soluud-text-primary hover:bg-soluud-primary-light rounded-lg border bg-white px-3 py-2 text-sm font-medium shadow-sm transition-all duration-150 active:scale-[0.98] sm:px-4"
        >
          <Trans>Today</Trans>
        </button>
        <button
          type="button"
          onClick={goNext}
          className="bg-soluud-primary hover:bg-soluud-primary-hover active:bg-soluud-primary-active flex items-center justify-center rounded-lg px-3 py-2 text-white shadow-sm transition-all duration-150 active:scale-[0.98]"
          aria-label="Next"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {hasEvents ? (
        <div className="border-soluud-border-subtle overflow-hidden rounded-xl border bg-white shadow-[--shadow-card]">
          <div
            style={
              {
                "--hour-row-height": `${hourRowHeightRem}rem`,
              } as React.CSSProperties
            }
          >
            <div className="flex">
              <Timeline
                startHour={timelineStartHour}
                endHour={timelineEndHour}
                locale={locale}
              />
              {displayDates.map((day) => (
                <DayColumn
                  key={day.toISOString()}
                  day={day}
                  events={events}
                  timelineStartHour={timelineStartHour}
                  timelineEndHour={timelineEndHour}
                  hourRowHeightRem={hourRowHeightRem}
                  locale={locale}
                  onEventClick={onEventClick}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="border-soluud-border-subtle overflow-hidden rounded-xl border bg-white shadow-[--shadow-card]">
          {/* Same header row as the grid, so the day stays visible */}
          <div className="flex">
            <div className="w-12 flex-shrink-0 border-r border-gray-200 sm:w-16">
              <div className="h-16 border-b border-gray-200" />
            </div>
            {displayDates.map((day) => (
              <div
                key={day.toISOString()}
                className="min-w-0 flex-1 border-r border-gray-200 last:border-r-0"
              >
                <DayHeader day={day} locale={locale} />
              </div>
            ))}
          </div>
          <div className="text-soluud-text-secondary flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
            <CalendarOff className="text-soluud-text-tertiary h-8 w-8" />
            <p className="text-sm">{emptyLabel ?? <Trans>No events</Trans>}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default Calendar;
